import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import distributorService from '../../../../services/DistributorService';
import { useSubscription } from '../../../../contexts/SubscriptionContext';
import '../../../../styles/tables.css';

const Plans = () => {
  const navigate = useNavigate();
  const { subscription, refreshSubscription } = useSubscription();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');

  const [subscribing, setSubscribing] = useState(false);
  const [subscribingPlanId, setSubscribingPlanId] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, [billingCycle]);

  const fetchPlans = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await distributorService.getPlans({
        billing_cycle: billingCycle,
        page_size: 100
      });

      if (result.success) {
        setPlans(result.data.results);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Free';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleSubscribe = async (plan) => {
    if (!plan) return;

    setSubscribing(true);
    setSubscribingPlanId(plan.id);

    try {
      const result = await distributorService.subscribeToPlan(plan.id);

      if (result.success) {
        toast.success('Subscription initiated! Redirecting to payment...');

        // Refresh subscription status
        await refreshSubscription();

        // Store subscription data for payment processing
        const subscriptionData = result.data;

        // Navigate to payment page or show payment modal
        if (subscriptionData && subscriptionData.subscription_id) {
          navigate('/distributor/subscription/my-subscription');
        }
      } else {
        toast.error(result.error);
      }
    } catch (err) {
      toast.error('Failed to subscribe to plan');
    } finally {
      setSubscribing(false);
      setSubscribingPlanId(null);
    }
  };

  const isCurrentPlan = (plan) => {
    return subscription?.plan_id === plan.id && subscription?.status === 'active';
  };

  const getBillingCycleLabel = (cycle) => {
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly'
    };
    return labels[cycle] || cycle;
  };

  const getSavingsPercentage = (plan) => {
    // Calculate savings for quarterly/yearly compared to monthly
    if (plan.billing_cycle === 'yearly') {
      return '20% OFF';
    } else if (plan.billing_cycle === 'quarterly') {
      return '10% OFF';
    }
    return null;
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Loading plans...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="text-center mb-5">
        <h2 className="fw-bold mb-2" style={{ color: '#4B0082' }}>
          Choose Your Plan
        </h2>
        <p className="text-muted mb-4">
          Select the plan that best fits your business needs
        </p>

        {/* Billing Cycle Toggle */}
        <div className="d-flex justify-content-center">
          <div
            className="btn-group"
            role="group"
            style={{
              borderRadius: '30px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            {['monthly', 'quarterly', 'yearly'].map((cycle) => (
              <Button
                key={cycle}
                variant={billingCycle === cycle ? 'primary' : 'light'}
                onClick={() => setBillingCycle(cycle)}
                style={{
                  padding: '10px 24px',
                  border: 'none',
                  fontWeight: billingCycle === cycle ? '600' : '400',
                  background: billingCycle === cycle
                    ? 'linear-gradient(135deg, #4B0082 0%, #6B238E 100%)'
                    : '#fff',
                  color: billingCycle === cycle ? '#fff' : '#333'
                }}
              >
                {getBillingCycleLabel(cycle)}
                {cycle === 'yearly' && (
                  <Badge bg="success" className="ms-2" style={{ fontSize: '0.7rem' }}>Save 20%</Badge>
                )}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Plans Grid */}
      <Row className="justify-content-center">
        {plans.length === 0 ? (
          <Col md={6}>
            <Alert variant="info" className="text-center">
              <i className="bi bi-info-circle me-2"></i>
              No plans available for {getBillingCycleLabel(billingCycle)} billing cycle.
            </Alert>
          </Col>
        ) : (
          plans.map((plan, index) => {
            const limits = plan.limits || {};
            const slaDetails = plan.sla_details || {};
            const planFeatures = plan.features || {};

            // Build features list with included/not included status
            const allFeatures = [
              { label: `Up to ${limits.max_retailers || 'Unlimited'} retailers`, included: true },
              { label: `Monthly turnover up to ${limits.max_monthly_turnover ? `₹${(parseFloat(limits.max_monthly_turnover) / 10000000).toFixed(0)} Cr` : 'Unlimited'}`, included: true },
              { label: 'Complete DMS access', included: planFeatures.dms_access },
              { label: 'Distributor dashboard', included: planFeatures.distributor_dashboard },
              { label: 'Retailer payment system', included: planFeatures.retailer_payment_system },
              { label: 'CA login access', included: planFeatures.ca_login },
              { label: 'Compliance reports', included: planFeatures.compliance_reports },
              { label: `${slaDetails.escalation_response_hours || 24}h escalation response`, included: !!slaDetails.escalation_response_hours },
              { label: `Up to ${limits.max_beat_users || 0} beat users`, included: !!limits.max_beat_users },
              { label: `${limits.max_support_ids || 0} support IDs`, included: !!limits.max_support_ids },
            ];

            return (
              <Col key={plan.id} lg={4} md={6} className="mb-4">
                <div
                  className="h-100 position-relative"
                  style={{
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    background: '#fff',
                    transition: 'transform 0.3s, box-shadow 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-10px)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(138, 43, 226, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,0,0,0.1)';
                  }}
                >
                  {/* Purple Wave Header */}
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 50%, #6D28D9 100%)',
                      height: '140px',
                      position: 'relative',
                      borderRadius: '20px 20px 0 0'
                    }}
                  >
                    {/* Wave Shape */}
                    <svg
                      viewBox="0 0 500 150"
                      preserveAspectRatio="none"
                      style={{
                        position: 'absolute',
                        bottom: '-1px',
                        left: 0,
                        width: '100%',
                        height: '60px'
                      }}
                    >
                      <path
                        d="M0,150 L0,40 Q250,120 500,40 L500,150 Z"
                        fill="#ffffff"
                      />
                    </svg>

                    {/* Current Plan Badge */}
                    {isCurrentPlan(plan) && (
                      <div className="position-absolute top-0 end-0 m-2">
                        <Badge bg="success" className="px-2 py-1">
                          <i className="bi bi-check-circle me-1"></i>
                          Active
                        </Badge>
                      </div>
                    )}

                    {/* Price Circle */}
                    <div
                      className="position-absolute start-50 translate-middle-x d-flex align-items-center justify-content-center"
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        background: '#fff',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                        top: '50px',
                        zIndex: 2
                      }}
                    >
                      <div className="text-center">
                        <span className="d-block fw-bold" style={{ fontSize: '1.5rem', color: '#7C3AED' }}>
                          {formatCurrency(plan.price).replace('₹', '₹')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="px-4 pb-4" style={{ paddingTop: '50px' }}>
                    {/* Plan Name */}
                    <div className="text-center mb-4">
                      <h4 className="fw-bold mb-1" style={{ color: '#1F2937', letterSpacing: '1px' }}>
                        {plan.name.toUpperCase()}
                      </h4>
                      <span className="text-muted" style={{ fontSize: '0.85rem', letterSpacing: '2px' }}>
                        PER {plan.billing_cycle.toUpperCase()}
                      </span>
                      {getSavingsPercentage(plan) && (
                        <Badge bg="success" className="ms-2">{getSavingsPercentage(plan)}</Badge>
                      )}
                    </div>

                    {/* Features List */}
                    <ul className="list-unstyled mb-4">
                      {allFeatures.map((feature, idx) => (
                        <li key={idx} className="d-flex align-items-center mb-2">
                          {feature.included ? (
                            <i className="bi bi-check-lg me-3" style={{ color: '#10B981', fontSize: '1.1rem' }}></i>
                          ) : (
                            <i className="bi bi-x-lg me-3" style={{ color: '#EF4444', fontSize: '0.9rem' }}></i>
                          )}
                          <span style={{
                            color: feature.included ? '#374151' : '#9CA3AF',
                            fontSize: '0.9rem'
                          }}>
                            {feature.label}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* Subscribe Button */}
                    <div className="d-grid">
                      {isCurrentPlan(plan) ? (
                        <Button
                          variant="success"
                          disabled
                          className="py-2 fw-semibold"
                          style={{
                            borderRadius: '25px',
                            letterSpacing: '1px'
                          }}
                        >
                          CURRENT PLAN
                        </Button>
                      ) : (
                        <Button
                          variant="outline-primary"
                          onClick={() => handleSubscribe(plan)}
                          disabled={subscribing}
                          className="py-2 fw-semibold"
                          style={{
                            borderRadius: '25px',
                            borderColor: '#7C3AED',
                            borderWidth: '2px',
                            color: '#7C3AED',
                            letterSpacing: '1px',
                            transition: 'all 0.3s'
                          }}
                          onMouseEnter={(e) => {
                            if (!subscribing) {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)';
                              e.currentTarget.style.color = '#fff';
                              e.currentTarget.style.borderColor = 'transparent';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!subscribing) {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = '#7C3AED';
                              e.currentTarget.style.borderColor = '#7C3AED';
                            }
                          }}
                        >
                          {subscribing && subscribingPlanId === plan.id ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              PROCESSING...
                            </>
                          ) : (
                            <>
                              ORDER NOW <i className="bi bi-chevron-right ms-1"></i>
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
            );
          })
        )}
      </Row>

    </Container>
  );
};

export default Plans;
